// MST
import { types, flow, cast } from 'mobx-state-tree'

import {
  IHomeBannerGetAllAttrs,
  IHomeBannerGetAttrs,
  IHomeBannerSaveAttrs,
} from 'interfaces/back-office/content-management/home/banner/home-banner-view.interface'
import {
  IHomeBannerApiGetAllResponse,
  IHomeBannerApiGetResponse,
  IHomeBannerApiPaginatedResponse,
  IHomeBannerApiSaveResponse,
} from 'interfaces/back-office/content-management/home/banner/home-banner-api.interface'
import { IHomeBannerDetail } from 'interfaces/back-office/content-management/home/banner'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_baseResponsePaginationTypes } from 'models/extensions/with-helper'

// Lodash
import omit from 'lodash/omit'
import { dateUtils_formatDate } from 'utils/common/date.utils'

// Home Banner Props
const homeBannerProps = {
  number: types.maybeNull(types.number),
  id: types.maybeNull(types.number),
  title: types.maybeNull(types.string),
  content: types.maybeNull(types.string),
  image: types.maybeNull(types.string),
  status: types.maybeNull(types.boolean),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
}

// Home Banner List Model
const HomeBanners = types
  .model('HomeBanners')
  .props(withHelper_baseResponsePaginationTypes(types.model('HomeBannerList').props(homeBannerProps)))

// Home Banner Detail Model
const HomeBannerDetail = types.model('HomeBannerDetail').props(omit(homeBannerProps, ['number']))

const HomeBannerStoreModel = types
  .model('HomeBanner')
  .props({
    homeBanner_isLoading: types.optional(types.boolean, false),
    homeBanner_isSaveLoading: types.optional(types.boolean, false),
    homeBanner_homeBanners: types.optional(HomeBanners, {}),
    homeBanner_detail: types.optional(HomeBannerDetail, {}),
  })
  .actions(self => ({
    homeBanner_SET_LOADING: (payload: boolean): void => {
      self.homeBanner_isLoading = payload
    },
    homeBanner_SET_SAVE_LOADING: (payload: boolean): void => {
      self.homeBanner_isSaveLoading = payload
    },
    homeBanner_SET_HOME_BANNERS: (payload: IHomeBannerApiPaginatedResponse): void => {
      const mapPayload = {
        ...payload,
        list: payload.list.map(list => {
          return {
            ...list,
            createdAt: dateUtils_formatDate(list.createdAt!, { tableFormat: true }),
            updatedAt: dateUtils_formatDate(list.updatedAt!, { tableFormat: true }),
          }
        }),
      }

      self.homeBanner_homeBanners = cast(mapPayload)
    },
    homeBanner_SET_HOME_BANNER: (payload: IHomeBannerDetail): void => {
      self.homeBanner_detail = cast(payload)
    },
    homeBanner_CLEAR_HOME_BANNERS: (): void => {
      self.homeBanner_homeBanners = cast({})
    },
    homeBanner_CLEAR_HOME_BANNER: (): void => {
      self.homeBanner_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch homeBanners
     *
     * @param {IHomeBannerGetAllAttrs} payload
     *
     * @return {any} any
     */
    homeBanner_fetchHomeBanners: flow(function* (payload: IHomeBannerGetAllAttrs) {
      self.homeBanner_SET_LOADING(true)

      const response: IHomeBannerApiGetAllResponse = yield self.environment.homeBannerApi.homeBanner_getHomeBanners({
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) self.homeBanner_SET_HOME_BANNERS(response.res.data)

      self.homeBanner_SET_LOADING(false)
    }),

    /**
     * @description Fetch home banner
     *
     * @param {IHomeBannerGetAttrs} payload
     *
     * @return {any} any
     */
    homeBanner_fetchHomeBanner: flow(function* (payload: IHomeBannerGetAttrs) {
      self.homeBanner_SET_LOADING(true)

      const response: IHomeBannerApiGetResponse = yield self.environment.homeBannerApi.homeBanner_getHomeBanner({
        params: { id: payload.params.id },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        self.homeBanner_SET_HOME_BANNER(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.homeBanner_SET_LOADING(false)
    }),

    /**
     * @description Save home banner
     *
     * @param {IHomeBannerSaveAttrs} payload
     *
     * @return {any} any
     */
    homeBanner_saveHomeBanner: flow(function* (payload: IHomeBannerSaveAttrs) {
      self.homeBanner_SET_SAVE_LOADING(true)

      const response: IHomeBannerApiSaveResponse = yield self.environment.homeBannerApi.homeBanner_saveHomeBanner({
        params: { ...payload.params },
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.homeBanner_SET_SAVE_LOADING(false)
    }),
  }))

export const createHomeBannerStoreDefaultModel = () => types.optional(HomeBannerStoreModel, {})
